import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { useTenant } from '../../lib/api/hooks.js';
import { SpendInsights } from './SpendInsights.js';
import { SpendBreakdown } from './SpendBreakdown.js';
import { VolumeChart, type SeriesPoint } from '../../components/charts/Charts.js';
import { ErrorBanner, Stat, StatGroup, CardHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { formatMoney, formatNumber, formatDuration } from '../../lib/format.js';

/**
 * Where this account's money went.
 *
 * Assembled entirely from endpoints that already existed and had no consumer:
 * /cdrs/stats, /cdrs/series, /cdrs/insights and /cdrs/breakdown, plus the two
 * Spend components already rendered on the dashboard. Nothing here re-derives a
 * figure -- every number on the page comes from the same aggregation over
 * cdrs.sell_amount that the rest of the platform bills from.
 *
 * That last point is deliberate and load-bearing. A usage screen that re-rated
 * calls to show them would eventually disagree with what was actually charged,
 * and the customer would be right to believe whichever number was larger.
 */

interface Stats {
  total_calls: number;
  answer_rate_pct: number;
  spend: string | null;
  currency: string;
  billable_sec?: number;
}
interface Billing {
  currency: string;
  balance: string;
  mode: string;
  sell_rate_deck_id: string | null;
  sell_rate_deck_name: string | null;
}

export function UsageTab({ tenantId }: { tenantId: string }) {
  const [days, setDays] = useState('30');
  const n = Number(days);
  /**
   * The setup warnings below are the provider's to fix, so they are for staff only (UX review,
   * 21 October 2026); the account's own people get one plain line instead. An own-trunk account's
   * calls are never rated by us, so "no sell rate deck" is no fault there.
   */
  const isAccount = useSession((s) => s.session?.scope.level === 'tenant' || s.session?.scope.level === 'site');
  const byot = useTenant(tenantId).data?.trunk_mode === 'byot';

  const stats = useQuery({
    queryKey: ['cdr-stats', tenantId, days],
    queryFn: () => api.get<Stats>(`/v1/tenants/${tenantId}/cdrs/stats?days=${days}`),
  });
  const series = useQuery({
    queryKey: ['cdr-series', tenantId, days],
    queryFn: () => api.get<{ data: SeriesPoint[] }>(
      `/v1/tenants/${tenantId}/cdrs/series?days=${days}`),
  });
  const billing = useQuery({
    queryKey: ['billing', tenantId],
    queryFn: () => api.get<Billing>(`/v1/tenants/${tenantId}/billing`),
    retry: false,   // a tenant with no billing account 404s, and that is an answer
  });

  if (stats.error) return <ErrorBanner error={stats.error} />;

  const s = stats.data;
  const points = series.data?.data ?? [];
  const spend = points.reduce((a, p) => a + Number(p.spend || 0), 0);
  const busiest = points.reduce<SeriesPoint | null>(
    (a, p) => (!a || p.total > a.total ? p : a), null);

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Usage breakdown</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            What was spent, on what, and by whom.
          </p>
        </div>
        <select className="arc-select" style={{ width: 'auto' }} value={days}
          onChange={(e) => setDays(e.target.value)} aria-label="Reporting period">
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      {/*
        A tenant whose calls are not being rated shows zeros that look like "a
        quiet month" rather than "nothing is being charged". The cause is a
        missing rate deck and it is otherwise invisible, so it is said here.
      */}
      {!isAccount && !byot && billing.data && !billing.data.sell_rate_deck_id && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            This account has no sell rate deck, so its calls are <strong>not being
            rated</strong>. Everything below will read zero however much they call.
          </span>
        </div>
      )}
      {!isAccount && billing.isError && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            This account has no billing account at all, so nothing is being
            charged. It was created before accounts were opened automatically.
          </span>
        </div>
      )}

      {isAccount && !byot && ((billing.data && !billing.data.sell_rate_deck_id) || billing.isError) && (
        <p className="arc-hint" style={{ margin: '0 0 14px' }}>
          Call costs are not available for this account yet. Your provider can set them up.
        </p>
      )}

      <StatGroup>
        <Stat bare label={`Spend (${n} days)`}
          value={formatMoney(String(spend), s?.currency ?? billing.data?.currency ?? 'ZAR')} />
        <Stat bare label="Calls" value={formatNumber(s?.total_calls ?? 0)}
          sub={s ? `${s.answer_rate_pct}% answered` : undefined} />
        <Stat bare label="Talk time"
          value={formatDuration(points.reduce((a, p) => a + p.duration_sec, 0))} />
        <Stat bare label="Balance"
          value={billing.data ? formatMoney(billing.data.balance, billing.data.currency) : '—'}
          sub={billing.data?.mode} />
      </StatGroup>

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <CardHead title="Daily volume" sub={busiest && busiest.total > 0
          ? `busiest ${new Date(busiest.day).toLocaleDateString('en-ZA')} · ${busiest.total} calls` : undefined} />
        <div className="arc-card-body">
          {points.length === 0 || points.every((p) => p.total === 0) ? (
            <p className="arc-subtle" style={{ margin: 0 }}>
              No calls in this period.
            </p>
          ) : (
            <VolumeChart data={points} />
          )}
        </div>
      </div>

      {/* Both already existed and were only rendered on the dashboard. */}
      <div style={{ marginBottom: 14 }}>
        <SpendBreakdown tenantId={tenantId} days={n} />
      </div>
      <SpendInsights tenantId={tenantId} days={n} />

      <p className="arc-hint" style={{ marginTop: 14 }}>
        Figures are the amounts actually charged to this account, not a fresh
        calculation — so they always match the ledger.
        {billing.data?.sell_rate_deck_name && (
          <> Rated on {billing.data.sell_rate_deck_name}.</>
        )}
      </p>
    </>
  );
}
